import { Anchor, Group, Stack, Text } from '@mantine/core'
import { Link } from 'react-router-dom'
import { MetaList, SectionCard } from '@lighthouse/design'
import { NOT_MEASURED, exactNumber } from '../../utils/format'
import { SignalSeverity } from './SignalBits'
import {
  excludedText, inputAsOfText, ruleEvaluationText, ruleName, ruleOutcome, subjectKindLabel,
} from './signalFormat'
import type { RuleOutcomeTone } from './signalFormat'
import type { SignalRule, SignalSummary } from '../../types/signals'

/** Where the stale rule's unsignalled runs are listed. */
const STALE_RUNS = '/execution-history?status=stale'

/**
 * "What is watched" — one row per rule the SERVER served, in its order. The SPA lists no rules of its own: a rule
 * this page invented could read as watched when no detector evaluates it.
 *
 * Each row is the catalogue's own words (trigger, threshold, auto-resolve, limits — null until a detector has
 * published them, and a dash then) beside what happened when the rule last ran. Nothing here is green: an
 * evaluated rule is plain text, a rule that could not read its input is amber, and one that has not reported is a
 * dash.
 */
export function RulesPanel({ summary }: { summary: SignalSummary }) {
  return (
    <SectionCard
      title="What is watched"
      subtitle="The rules the detector publishes, as it last evaluated them. Each restates something another page already measures."
    >
      <Stack gap="lg" data-signal-rules="">
        {summary.rules.map((rule) => <RuleRow key={rule.ruleId} rule={rule} />)}
      </Stack>
    </SectionCard>
  )
}

const TONE_COLOUR: Record<RuleOutcomeTone, string> = {
  ok: 'var(--color-text-secondary)',
  partial: 'var(--color-text-secondary)',
  problem: 'var(--status-warning-fg)',
  unmeasured: 'var(--color-text-disabled)',
}

function RuleRow({ rule }: { rule: SignalRule }) {
  const outcome = ruleOutcome(rule)
  const excluded = excludedText(rule)
  const open = rule.openUnconfirmedCount > 0
    ? `${exactNumber(rule.openCount)} open · ${exactNumber(rule.openUnconfirmedCount)} unconfirmed`
    : `${exactNumber(rule.openCount)} open`

  return (
    <Stack gap={6} data-rule={rule.ruleId} data-rule-outcome={outcome.tone}>
      <Group gap="xs" justify="space-between" wrap="wrap">
        <Group gap="xs" wrap="wrap">
          <SignalSeverity severity={rule.severity} />
          <Text fw={600} size="sm">{ruleName(rule)}</Text>
          <Text size="xs" c="dimmed" ff="monospace">{rule.ruleId}</Text>
          <Text size="xs" c="dimmed">· {subjectKindLabel(rule.subjectKind)}</Text>
        </Group>
        <Text size="sm" fw={500}>{open}</Text>
      </Group>

      <Text size="xs" style={{ color: TONE_COLOUR[outcome.tone] }}>
        {outcome.tone === 'unmeasured' && <span aria-hidden="true">{NOT_MEASURED} </span>}
        <strong>{outcome.label}.</strong> {ruleEvaluationText(rule)} · {inputAsOfText(rule)}
      </Text>

      {excluded && (
        <Text size="xs" data-rule-excluded="">
          {rule.subjectKind === 'EtlRun'
            ? <Anchor component={Link} to={STALE_RUNS} size="xs">{excluded}</Anchor>
            : excluded}
        </Text>
      )}

      <MetaList
        items={[
          { label: 'Trigger', value: rule.trigger ?? `${NOT_MEASURED} not published` },
          { label: 'Threshold', value: rule.threshold ?? `${NOT_MEASURED} not published` },
          { label: 'Auto-resolve', value: rule.autoResolve ?? `${NOT_MEASURED} not published` },
          { label: 'Dedupe key', value: rule.dedupeKey ?? `${NOT_MEASURED} not published` },
          { label: 'Limits', value: rule.limits ?? `${NOT_MEASURED} not published` },
        ]}
      />
    </Stack>
  )
}
